import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Save as SaveIcon,
  Create as SignIcon,
  ArrowBack as BackIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  Article as ArticleIcon,
  Person as PersonIcon,
  MedicalServices as MedicalIcon
} from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import { MEDICAL_BILLING_PERSON, secureApi } from "../config";
import { Context } from "../context";
import TopNav from "../components/topnav";

const useStyles = makeStyles((theme) => ({
  formContainer: {
    paddingTop: 20,
    paddingBottom: 40,
  },
  pageHeader: {
    marginBottom: 30,
    textAlign: "center",
  },
  formTitle: {
    marginBottom: 20,
    textAlign: "center",
    fontWeight: "500",
  },
  bold: {
    fontWeight: "bold",
  },
  underline: {
    textDecoration: "underline",
  },
  boldUnderline: {
    fontWeight: "bold",
    textDecoration: "underline",
  },
  signatureBox: {
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: 10,
    minHeight: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  signatureImg: {
    maxHeight: "50px",
    maxWidth: "100%",
  },
  signatureCanvas: {
    border: "1px solid #ccc",
    borderRadius: 4,
  },
  signatureSection: {
    marginTop: 20,
    marginBottom: 20,
  },
  actionButtons: {
    marginTop: 30,
  },
  riskItem: {
    marginBottom: 8,
  },
  stepperContainer: {
    marginBottom: 30,
  }
}));

const today = new Date();
const year = today.getFullYear();
const month = String(today.getMonth() + 1).padStart(2, "0");
const date = String(today.getDate()).padStart(2, "0");
const formattedDate = `${month}/${date}/${year}`;

const PATIENT_SIGN_TYPE = "patient";
const WITNESS_SIGN_TYPE = "witness";
const MEDICAL_PROVIDER_SIGN_TYPE = "medical provider";

const ConsentForm = () => {
  const classes = useStyles();
  const { patient_id, index } = useParams();
  const navigate = useNavigate();
  const { setLoggedIn, user } = useContext(Context);

  useEffect(() => {
    if (user && user.role === MEDICAL_BILLING_PERSON) {
      toast.warn("You don't have permission to access this page.");
      navigate("/");
    }
  }, [user, navigate]);

  const [activeStep, setActiveStep] = useState(0);
  const [patient, setPatient] = useState({});
  const [procedure, setProcedure] = useState("");
  const [medicalProviderName, setMedicalProviderName] = useState("");
  const [patientSignature, setPatientSignature] = useState("");
  const [witnessSignature, setWitnessSignature] = useState("");
  const [medicalProviderSignature, setMedicalProviderSignature] = useState("");
  const [patientSignatureDate, setPatientSignatureDate] = useState(formattedDate);
  const [medicalProviderSignatureDate, setMedicalProviderSignatureDate] = useState(formattedDate);
  const [signPad, setSignPad] = useState({});
  const [signed, setSigned] = useState(false);
  const [signType, setSignType] = useState();
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleClickPatientSign = () => {
    setSignType(PATIENT_SIGN_TYPE);
    setSignModalOpen(true);
  };

  const handleClickWitnessSign = () => {
    setSignType(WITNESS_SIGN_TYPE);
    setSignModalOpen(true);
  };

  const handleClickMedicalProviderSign = () => {
    setSignType(MEDICAL_PROVIDER_SIGN_TYPE);
    setSignModalOpen(true);
  };

  const handleClear = () => {
    signPad.clear();
    setSigned(false);
  };

  const handleSignModalClose = (event, reason) => {
    if (reason !== "backdropClick") {
      handleClear();
      setSignModalOpen(false);
    }
  };

  const handleSign = () => {
    const physicalSign = signPad.getTrimmedCanvas().toDataURL("image/png");
    if (signType === PATIENT_SIGN_TYPE) {
      setPatientSignature(physicalSign);
      if (patientSignatureDate.trim() === "")
        setPatientSignatureDate(formattedDate);
    } else if (signType === WITNESS_SIGN_TYPE) {
      setWitnessSignature(physicalSign);
    } else if (signType === MEDICAL_PROVIDER_SIGN_TYPE) {
      setMedicalProviderSignature(physicalSign);
      if (medicalProviderSignatureDate.trim() === "")
        setMedicalProviderSignatureDate(formattedDate);
    }

    handleSignModalClose();
  };

  const handleSave = () => {
    setLoading(true);
    const formData = {
      patientName: `${patient.patientInfo.first_name} ${patient.patientInfo.last_name}`,
      procedure,
      patientSignature,
      patientSignatureDate,
      witnessSignature,
      medicalProviderName,
      medicalProviderSignature,
      medicalProviderSignatureDate,
    };

    const apiCall = parseInt(index) >= 0
      ? secureApi(localStorage.getItem("alex-med-token")).put(`/consent-form/${patient_id}/${index}`, formData)
      : secureApi(localStorage.getItem("alex-med-token")).post(`/consent-form/${patient_id}`, formData);

    apiCall
      .then(() => {
        const message = parseInt(index) >= 0
          ? "Consent form has been updated successfully!"
          : "Consent form has been saved successfully!";
        toast.success(message);
        navigate(`/patient/${patient_id}`);
      })
      .catch((e) => {
        console.log(e);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else if (e.response) {
          toast.error(e.response.data.message);
        } else {
          toast.error("An error occurred while saving the form.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (patient_id) {
      setLoading(true);
      secureApi(localStorage.getItem("alex-med-token"))
        .get(`patient/${patient_id}`)
        .then((response) => {
          const { data: patient } = response;
          setPatient(patient);

          if (parseInt(index) >= 0) {
            if (patient && patient.consentForm && patient.consentForm[index]) {
              const form = patient.consentForm[index];
              setProcedure(form.procedure || "");
              setMedicalProviderName(form.medicalProviderName || "");
              setPatientSignature(form.patientSignature || "");
              setWitnessSignature(form.witnessSignature || "");
              setMedicalProviderSignature(form.medicalProviderSignature || "");
              setPatientSignatureDate(form.patientSignatureDate || formattedDate);
              setMedicalProviderSignatureDate(form.medicalProviderSignatureDate || formattedDate);
            } else {
              toast.warn("Corresponding consent form doesn't exist");
              navigate(`/patient/${patient_id}`);
            }
          }
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message);
          } else {
            toast.error("An error occurred while loading patient data.");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      toast.warn("Invalid patient ID!");
      navigate("/");
    }
  }, [patient_id, index, navigate, setLoggedIn]);

  const steps = ['Review Information', 'Procedures & Risks', 'Signatures'];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderHeaderSection();
      case 1:
        return renderRisksSection();
      case 2:
        return renderSignatureSection();
      default:
        return 'Unknown step';
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderHeaderSection = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Informed Consent for Pain Procedures
      </Typography>
      
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body1" paragraph>
            You have a pain problem that has not been relieved by routine
            treatments. A procedure, specifically an injection or operation, is
            now indicated for further evaluation or treatment of your pain. There
            is{` `}
            <span className={classes.boldUnderline}>NO</span>
            <span className={classes.bold}>{` guarantee `}</span>that a
            procedure will cure your pain, and in rare cases,
            <span className={classes.bold}>{` it could become WORSE`}</span>,
            even when the procedure is performed in a technically perfect manner.
            The degree and duration of pain relief varies from person to person,
            so after your procedure, we will reevaluate your progress, then
            determine if further treatment is necessary.
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Tell the physicians if you are taking any blood thinners such as
              <strong>{` PLAVIX, Aspirin, Coumadin, Lovenox or HEPARIN`}</strong>,
              as these can cause excessive bleeding and a procedure should{` `}
              <span className={classes.boldUnderline}>NOT</span>
              {` `}be performed.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            Alternatives to the procedure include medications, physical therapy, 
            acupuncture, surgery, etc. Benefits include increased likelihood of 
            correct diagnosis and/or of decrease or elimination of pain.
          </Typography>
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="flex-end">
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue to Risks
        </Button>
      </Box>
    </>
  );

  const renderRisksSection = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Procedure Details and Risks
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Procedure"
            variant="outlined"
            fullWidth
            value={procedure}
            onChange={(e) => setProcedure(e.target.value)}
            placeholder="Enter procedure details"
          />
        </Grid>
      </Grid>
      
      <Card elevation={2} sx={{ mt: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }} className={classes.boldUnderline}>
            Risks
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className={classes.riskItem}>
                <span className={classes.bold}>• Increased pain and allergic reaction </span>
                from local anesthetics, iodine, contrast (X-Ray dye), materials
                containing latex, IV anesthetics and/or other medications
              </Typography>
              
              <Typography className={classes.riskItem}>
                <span className={classes.bold}>• Allergic reaction from steroid; </span>
                facial flushing, elevation in blood glucose, headache, increased
                appetite, weight gain, swelling, menstrual irregularities,
                hoarseness, numbness, infection, abnormal heartbeats, increased
                blood pressure, stroke, heart attack, insomnia, etc.
              </Typography>
              
              <Typography className={classes.riskItem}>
                <span className={classes.bold}>• Infection </span>
                on skin, tissue, bones, joints, discs, nerves, ligaments, possibly blood
                stream (Sepsis), brain and spinal cord (Meningitis) may require
                hospitalization
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography className={classes.riskItem}>
                <span className={classes.bold}>• Bleeding </span>
                into epidural space (Epidural Hematoma) and into spinal canal (Spinal Hematoma)
                may require surgical interventions such as an evacuation of blood
                from epidural space or spinal canal and decompression surgery
              </Typography>
              
              <Typography className={`${classes.bold} ${classes.riskItem}`}>
                • Nerve damage, nerve injury, tissue injury, tissue damage, temporary
                and permanent numbness and/or weakness, paralysis, spinal cord
                injury, urinary and/or fecal incontinence
              </Typography>
              
              <Typography className={classes.riskItem}>
                <span className={classes.bold}>• Headache </span>
                ("Spinal headache") may require blood patch (Injecting your own blood into
                epidural space) and hospitalization.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Typography className={classes.riskItem}>
                <span className={classes.bold}>
                  • Trigger Point Injection, Peripheral Nerve block, Occipital Nerve Block:
                </span>
                {` In addition to the above complications, `}
                <span className={classes.bold}>{`air in lung (Pneumothorax) `}</span>
                requiring chest tube in hospital, local pain from tissue and/or
                nerve irritation, dimpling of/depression in skin.
              </Typography>
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              The incidence of serious complications listed above requiring
              treatment is low, but it may still occur. Your physician believes the
              benefits of the procedure outweigh its risks or it would not have been
              offered to you, and it is your decision and right to accept or decline
              to have the procedure done.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between">
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue to Signatures
        </Button>
      </Box>
    </>
  );

  const renderSignatureSection = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Consent and Signatures
      </Typography>
      
      <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="body1" paragraph>
            <span className={classes.boldUnderline}>
              I have read or had read to me
            </span>
            {` the above information including the Pre-Procedure Patient
            Instruction page. I `}
            <span className={classes.bold}>UNDERSTAND</span> there are risks
            involved with spinal procedure, to include rare complications, which
            may not have been specifically mentioned above.
          </Typography>
          
          <Typography variant="body1" paragraph>
            The risks have been explained to my satisfaction and I accept them and
            consent to any procedure which is performed by
            <span className={classes.bold}>{` Dr. Alexander Movshis. `}</span>
            I herein authorize physicians, nurse practitioners and their
            associates in Modal Medical Services PLLC to perform this procedure.
          </Typography>
          
          <Typography variant="body1" paragraph>
            I also understand that one of the greatest risks involved with pain
            management procedures involves various medications taken, allergies
            and my general medical condition. I will inform the doctor of any
            blood thinning medication taken or any changes in other medications,
            allergies or medical condition prior to any procedure.
          </Typography>
        </CardContent>
      </Card>
      
      <Paper elevation={3} className={classes.signatureSection}>
        <Grid container spacing={3} sx={{ p: 2 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" gutterBottom>
              Patient's Name
            </Typography>
            <TextField
              value={
                patient && patient.patientInfo
                  ? `${patient.patientInfo.first_name} ${patient.patientInfo.last_name}`
                  : ""
              }
              variant="outlined"
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" gutterBottom>
              Patient's Signature
            </Typography>
            <Box 
              className={classes.signatureBox}
              onClick={handleClickPatientSign}
              sx={{ cursor: 'pointer' }}
            >
              {patientSignature ? (
                <img
                  src={patientSignature}
                  alt="Patient signature"
                  className={classes.signatureImg}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Click to sign
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" gutterBottom>
              Date
            </Typography>
            <TextField
              value={patientSignatureDate}
              onChange={(e) => setPatientSignatureDate(e.target.value)}
              variant="outlined"
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" gutterBottom>
              Witness Signature
            </Typography>
            <Box 
              className={classes.signatureBox}
              onClick={handleClickWitnessSign}
              sx={{ cursor: 'pointer' }}
            >
              {witnessSignature ? (
                <img
                  src={witnessSignature}
                  alt="Witness signature"
                  className={classes.signatureImg}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Click to sign
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Medical Provider Declaration
      </Typography>
      
      <Typography variant="body1" paragraph>
        I and/or my associate have explained the procedure and the pertinent 
        contents of this document to the patient and have answered all the 
        patient's questions. To the best of my knowledge, the patient has been 
        adequately informed and the patient has consented to the above described 
        procedure.
      </Typography>
      
      <Paper elevation={3} className={classes.signatureSection}>
        <Grid container spacing={3} sx={{ p: 2 }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" gutterBottom>
              Medical Provider's Name
            </Typography>
            <TextField
              value={medicalProviderName}
              onChange={(e) => setMedicalProviderName(e.target.value)}
              variant="outlined"
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" gutterBottom>
              Medical Provider's Signature
            </Typography>
            <Box 
              className={classes.signatureBox}
              onClick={handleClickMedicalProviderSign}
              sx={{ cursor: 'pointer' }}
            >
              {medicalProviderSignature ? (
                <img
                  src={medicalProviderSignature}
                  alt="Medical Provider signature"
                  className={classes.signatureImg}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Click to sign
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" gutterBottom>
              Date
            </Typography>
            <TextField
              value={medicalProviderSignatureDate}
              onChange={(e) => setMedicalProviderSignatureDate(e.target.value)}
              variant="outlined"
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Box display="flex" justifyContent="space-between" className={classes.actionButtons}>
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Box>
          <Button
            variant="outlined"
            color="error"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Save Form
          </Button>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      <TopNav />
      <Container maxWidth="lg" className={classes.formContainer}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h4" className={classes.pageHeader}>
            Modal Medical Services PLLC
          </Typography>
          
          <Box className={classes.stepperContainer}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          
          {getStepContent(activeStep)}
        </Paper>
      </Container>
      
      <Dialog 
        open={signModalOpen} 
        onClose={handleSignModalClose} 
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {signType === PATIENT_SIGN_TYPE && (
            <Box display="flex" alignItems="center">
              <PersonIcon sx={{ mr: 1 }} />
              Patient Signature
            </Box>
          )}
          {signType === WITNESS_SIGN_TYPE && (
            <Box display="flex" alignItems="center">
              <ArticleIcon sx={{ mr: 1 }} />
              Witness Signature
            </Box>
          )}
          {signType === MEDICAL_PROVIDER_SIGN_TYPE && (
            <Box display="flex" alignItems="center">
              <MedicalIcon sx={{ mr: 1 }} />
              Medical Provider Signature
            </Box>
          )}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please sign in the space below:
          </Typography>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <SignatureCanvas
                penColor="black"
                canvasProps={{ 
                  width: 600, 
                  height: 200, 
                  className: "sigCanvas" 
                }}
                ref={(ref) => {
                  setSignPad(ref);
                }}
                onEnd={() => setSigned(true)}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button 
            onClick={handleClear} 
            variant="outlined" 
            color="warning"
            startIcon={<ClearIcon />}
          >
            Clear
          </Button>
          <Button 
            onClick={handleSign} 
            variant="contained" 
            disabled={!signed}
            endIcon={<SignIcon />}
            color="primary"
          >
            Confirm Signature
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConsentForm;